import BoxInfo from '@/components/boxInfo/boxInfo';
import { useParamsState } from '@/components/dataGrid/hooks/useParamsState/useParamsState';
import { usePaginatedList } from '@/components/dataGrid/hooks/useRequestData/usePaginatedList';
import EmptyMessage from '@/components/emptyMessage';
import useFileDownload from '@/modules/files/hooks/useFileDownload/useFileDownload';
import {
  MOVE_TYPE_COLLECTION,
  moveListFiltersInitialValues,
} from '@/modules/move/entities/constants/move';
import { MOVE_ROW_BENEFICIARY_STATUS } from '@/modules/move/entities/enums/move';
import {
  MoveListFilters,
  MoveReponseNormalized,
  MoveRequest,
} from '@/modules/move/entities/interfaces/move';
import { MoveService } from '@/modules/move/services';
import {
  MoveListFilter,
  MoveListFilterFieldKey,
} from '@/modules/move/views/moveListFilter/moveListFilter';
import { MoveListColumnKey, MoveListLine } from '@/modules/move/views/moveListLine/moveListLine';
import { useChangeState } from '@/shared/hooks/useChangeState';
import useCompany from '@/shared/hooks/useCompany/useCompany';
import ICONS from '@/shared/entities/constants/icons';
import { pick, random } from 'lodash';
import React from 'react';
import { Pagination, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

interface MoveListProps {
  fields: {
    key: MoveListFilterFieldKey;
    width: number;
  }[];
  columns: MoveListColumnKey[];
}

export const MoveList: React.FC<MoveListProps> = ({ fields, columns }) => {
  const { companyId } = useCompany();
  const { downloadFile, loading: loadingDownload } = useFileDownload();

  const [paramsState, setParamsState] = useParamsState<Partial<MoveListFilters>>({
    initialState: moveListFiltersInitialValues,
  });

  const extractMoveRequest = (
    filters: MoveListFilters,
    companyId: string,
  ): Partial<MoveRequest> => {
    const moveRequest: Partial<MoveRequest> = {
      ...pick(filters, Object.keys(moveListFiltersInitialValues)),
      'rows.beneficiaryStatus': structuredClone(filters['rows.beneficiaryStatus'])?.filter(Boolean),
      companyId,
    };

    if (
      moveRequest['rows.beneficiaryStatus']?.includes(MOVE_ROW_BENEFICIARY_STATUS.NOT_INITIALIZED)
    ) {
      moveRequest['rows.beneficiaryStatus'].push(MOVE_ROW_BENEFICIARY_STATUS.VALIDATED);
    }

    moveRequest.type = filters.action
      ? MOVE_TYPE_COLLECTION.filter((type) => type.includes(filters.action))
      : MOVE_TYPE_COLLECTION;

    return moveRequest;
  };

  const { data, page, pageSize, total, totalPages, loading } = usePaginatedList<
    MoveReponseNormalized,
    MoveListFilters
  >({
    source: (filters, companyId) =>
      new MoveService().getMoves<MoveReponseNormalized>(extractMoveRequest(filters, companyId)),
    companyId: companyId || '',
    paramsToFilter: paramsState,
    omitParamsToFilter: ['showMoreFilters'],
  });

  const changeParam = useChangeState(setParamsState);

  return (
    <Grid gap={24} column stretch>
      <MoveListFilter fields={fields} paramsState={paramsState} changeParam={changeParam} />

      {(loading || loadingDownload) &&
        Array.from({ length: 4 }).map(() => (
          <Text value='' rounded lines={7} key={random(0, 999)} />
        ))}

      {!loading &&
        !loadingDownload &&
        total > 0 &&
        data.map((move) => (
          <MoveListLine
            key={move.id}
            move={move}
            columns={columns}
            loading={loading}
            actionsByItem={[
              {
                redirectToDetail: ({ id }) =>
                  `/companies/${companyId}/beneficiaries/moves/${id}/details`,
              },
              {
                label: 'Baixar arquivo',
                icon: ICONS.DOWNLOAD,
                onClick: ({ id }) => downloadFile(id),
                loading: loading || loadingDownload,
              },
            ]}
          />
        ))}

      {!loading && !loadingDownload && total === 0 && (
        <BoxInfo>
          <EmptyMessage title='Nada por aqui!' description='Nenhum registro encontrado.' />
        </BoxInfo>
      )}

      {!loading && !loadingDownload && total > 0 && (
        <Pagination
          pageSize={pageSize}
          total={total}
          page={page}
          totalPages={totalPages}
          onChangePage={(page) => changeParam({ page })}
          onChangePageSize={(pageSize) => changeParam({ pageSize, page: 1 })}
        />
      )}
    </Grid>
  );
};
